import { useState } from 'react';
import { useEntitlements } from '../hooks/useEntitlements';
import { useProvisionBilling } from '../hooks/useTenants';
import type { Tenant } from '@policy-tracker/types';

interface ProvisionBillingActionProps {
    tenant: Pick<Tenant, 'tenantId' | 'name'>;
}

/**
 * Best-effort extraction of an error message thrown by the Amplify API client.
 * Amplify wraps backend 4xx/5xx responses as `{ response: { body: { json() } } }`
 * — the controller in #2071 surfaces Stripe errors via a `message` field on that
 * JSON body, which we want to render verbatim for operators (e.g.,
 * "No such price: price_oldid"). If extraction fails for any reason, falls back
 * to a generic "Failed to provision billing" message.
 */
async function extractErrorMessage(err: unknown): Promise<string> {
    const fallback = 'Failed to provision billing. Please try again.';
    try {
        const errAny = err as Record<string, unknown>;
        const response = errAny?.response as Record<string, unknown> | undefined;
        const body = response?.body;
        if (!body) return fallback;
        const parsed = typeof body === 'string' ? JSON.parse(body) : await (body as { json: () => Promise<unknown> }).json();
        const parsedRecord = parsed as Record<string, unknown>;
        const msg = parsedRecord.message;
        if (typeof msg === 'string' && msg.length > 0) {
            return msg;
        }
        return fallback;
    } catch {
        return fallback;
    }
}

/**
 * Per-row Provision Billing action for the TM operator tenant list.
 *
 * Visible only when:
 *  - entitlements have loaded
 *  - the tenant has no Stripe customer (`hasBillingAccount !== true` — covers
 *    both legacy tenants where the field is `false` and pre-replication
 *    tenants where the field is `undefined`)
 *  - the tenant is on a paid tier (`tier !== 'free'`)
 *
 * Click opens an inline confirmation panel (no modal library exists in this
 * UI). The operator chooses whether to create a subscription alongside the
 * Stripe customer (default `true`) and confirms; the request goes through
 * `useProvisionBilling`, which invalidates entitlements + tenants list on
 * success so the row reflects the new state. Errors are surfaced inline,
 * with Stripe's own message extracted from the response body where possible.
 */
export default function ProvisionBillingAction({ tenant }: ProvisionBillingActionProps) {
    const { data: entitlements, isLoading } = useEntitlements(tenant.tenantId, true);
    const provisionBilling = useProvisionBilling();

    const [open, setOpen] = useState(false);
    const [createSubscription, setCreateSubscription] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);

    if (isLoading || !entitlements) {
        return null;
    }
    if (entitlements.hasBillingAccount === true) {
        return null;
    }
    if (entitlements.tier === 'free') {
        return null;
    }

    const handleOpen = () => {
        setErrorMessage(null);
        setShowSuccess(false);
        setCreateSubscription(true);
        setOpen(true);
    };

    const handleCancel = () => {
        setOpen(false);
        setErrorMessage(null);
    };

    const handleConfirm = async () => {
        setErrorMessage(null);
        try {
            await provisionBilling.mutateAsync({
                tenantId: tenant.tenantId,
                body: { createSubscription, collectionMethod: 'send_invoice' },
            });
            setOpen(false);
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } catch (err: unknown) {
            const message = await extractErrorMessage(err);
            setErrorMessage(message);
        }
    };

    return (
        <div>
            {!open && (
                <button className="btn btn-outline" style={{ fontSize: '12px', padding: '0.25rem 0.5rem' }} onClick={handleOpen}>
                    Provision Billing
                </button>
            )}

            {open && (
                <div
                    style={{
                        marginTop: '0.25rem',
                        padding: '0.5rem',
                        border: '1px solid #ced4da',
                        borderRadius: '0.25rem',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                    }}
                >
                    <div style={{ marginBottom: '0.5rem' }}>
                        Provision Stripe billing for <strong>{tenant.name}</strong>?
                    </div>
                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                        <input
                            type="checkbox"
                            checked={createSubscription}
                            onChange={(e) => setCreateSubscription(e.target.checked)}
                            style={{ marginRight: '0.25rem' }}
                        />
                        Create subscription
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            className="btn"
                            onClick={handleConfirm}
                            disabled={provisionBilling.isPending}
                            style={{ fontSize: '12px', padding: '0.25rem 0.5rem' }}
                        >
                            {provisionBilling.isPending ? 'Provisioning...' : 'Confirm'}
                        </button>
                        <button
                            className="btn btn-outline"
                            onClick={handleCancel}
                            disabled={provisionBilling.isPending}
                            style={{ fontSize: '12px', padding: '0.25rem 0.5rem' }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="error" style={{ fontSize: '12px', marginTop: '0.25rem' }}>
                    {errorMessage}
                </div>
            )}

            {showSuccess && <div style={{ color: '#198754', fontSize: '12px', marginTop: '0.25rem' }}>Billing provisioned</div>}
        </div>
    );
}
